import { useState, useEffect, useReducer } from 'react';
import fnNoOp from './no-op';

type ReducedScreenWidthState = {
  isSmallMobileScreen: boolean;
  isMobileScreen: boolean;
  isMediumScreen: boolean; // >= 765px for NRCFooter compatibility
  isWideScreen: boolean; // > 990px for ReactTabs and Tabs compatibility
  isLargeScreen: boolean; // >= 1280px for Authenticated.tsx compatibility
  isTabletScreen: boolean;
  isDesktopScreen: boolean;
  isInitializing: boolean;
};

const screenWidthReducer = (state: ReducedScreenWidthState, width: number) => {
  const isSmallMobileScreen = width <= 640;
  const isMobileScreen = width < 1024;
  const isMediumScreen = width >= 765; // For NRCFooter compatibility
  const isWideScreen = width > 990; // For ReactTabs and Tabs compatibility
  const isLargeScreen = width >= 1280; // For Authenticated.tsx compatibility
  const isTabletScreen = width >= 1024 && width < 1440;
  const isDesktopScreen = width >= 1440;
  const isInitializing = false;

  return isSmallMobileScreen !== state.isSmallMobileScreen ||
    isMobileScreen !== state.isMobileScreen ||
    isMediumScreen !== state.isMediumScreen ||
    isWideScreen !== state.isWideScreen ||
    isLargeScreen !== state.isLargeScreen ||
    isTabletScreen !== state.isTabletScreen ||
    isDesktopScreen !== state.isDesktopScreen ||
    isInitializing !== state.isInitializing
    ? {
        isSmallMobileScreen,
        isMobileScreen,
        isMediumScreen,
        isWideScreen,
        isLargeScreen,
        isTabletScreen,
        isDesktopScreen,
        isInitializing,
      }
    : state;
};

/**
 * Simple hook used to store current screen width as react state.
 * @returns the current screen width
 */
const useScreenWidth = () => {
  const isClient = typeof window === 'object';

  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    const current = {
      timeout: undefined as ReturnType<typeof setTimeout> | undefined,
    };
    const handleResize = () => {
      delete current.timeout;
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    current.timeout = setTimeout(handleResize, 50);

    return () => {
      if (!!current.timeout) {
        clearTimeout(current.timeout);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isClient]);

  return screenWidth;
};

export const useScreenBreakpoint = () => {
  const [breakpoints, dispatchBreakpoints] = useReducer(screenWidthReducer, {
    isSmallMobileScreen: false,
    isMobileScreen: false,
    isMediumScreen: false,
    isWideScreen: false,
    isLargeScreen: false,
    isTabletScreen: false,
    isDesktopScreen: false,
    isInitializing: true,
  });

  useEffect(() => {
    const current = {
      timeout: undefined as ReturnType<typeof setTimeout> | undefined,
    };
    const handleResize = () => {
      delete current.timeout;
      dispatchBreakpoints(window.innerWidth);
    };
    window.addEventListener('resize', handleResize, {
      passive: true,
    });
    current.timeout = setTimeout(handleResize, 50);
    return () => {
      if (!!current.timeout) {
        clearTimeout(current.timeout);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [dispatchBreakpoints]);

  return breakpoints;
};

/**
 * Helper hook to check if the screen width is within a min and max range
 * @param breakpointMin Minimum screen width to be within breakpoint
 * @param breakpointMax Maximum screen width to be within breakpoint
 * @returns true if the current screen width is within the specified min and max range.
 */
export const useIsScreenWithinRange = (breakpointMin: number, breakpointMax: number) => {
  const isClient = typeof window === 'object';
  const [isSmallerThen, setIsSmallerThen] = useState(false);

  useEffect(() => {
    if (!isClient) {
      return fnNoOp;
    }

    const handleResize = () => {
      const isSmallerThenNow =
        window.innerWidth >= breakpointMin && window.innerWidth < breakpointMax;
      if (isSmallerThenNow !== isSmallerThen) {
        setIsSmallerThen(isSmallerThenNow);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpointMin, breakpointMax, isSmallerThen, isClient]);

  return isSmallerThen;
};

export default useScreenWidth;
